import type { AnalysisReport } from "@/api/analysis-report";
import type { DihedralProfile } from "@/api/affdo";
import type { ApiErrorKind } from "@/lib/api-error-kind";
import { fitAbsence, mmSeriesName, numbers } from "@/lib/dihedral-profile";

/**
 * The cross-run torsion overlay's model — selection codec plus the pure
 * transformation from N runs' analysis reports into one chart's worth of
 * series (VISUALIZATION_PLAN U2.1, the comparison half).
 *
 * Everything here is a pure function of (selection, per-run report state), so
 * the two rules the plan calls load-bearing are unit-testable without
 * mounting recharts:
 *
 * 1. **The URL is the contract.** `parseOverlaySelection` /
 *    `overlaySearchParams` round-trip a selection exactly, so a link reopens
 *    the same comparison.
 * 2. **A series is never fabricated, and never silently dropped.** Every
 *    (run, torsion) pair the selection asks for either produces a series with
 *    real points or produces exactly one {@link OverlayGap} saying, in the
 *    run's own terms, why it does not. A chart showing fewer curves than the
 *    link promised, with nothing saying why, is the failure mode this whole
 *    module is shaped around.
 */

export const OVERLAY_SERIES_KINDS = ["qm", "mm", "fit"] as const;
export type OverlaySeriesKind = (typeof OVERLAY_SERIES_KINDS)[number];

/** `qm` is the reference every scanned torsion has, so the default link
 * always draws something. `mm`/`fit` are legitimately absent for whole
 * classes of torsion and are opt-in through the URL. */
export const DEFAULT_SERIES_KIND: OverlaySeriesKind = "qm";

export const SERIES_KIND_LABEL: Record<OverlaySeriesKind, string> = {
  qm: "QM reference",
  mm: "MM initial",
  fit: "AFFDO fit",
};

export const RUNS_PARAM = "runs";
export const TORSIONS_PARAM = "torsions";
export const SERIES_PARAM = "series";

/**
 * What a single link may ask for (Rupert PR-993 r0; reconciled in
 * flowstack#994).
 *
 * The selection arrives from a URL that gets pasted around, and
 * `useAnalysisReports` maps it 1:1 onto `useQueries` — so without a cap,
 * `runs=` with a few thousand ids becomes a few thousand cookie-authenticated
 * GETs the moment a recipient clicks, plus a `<Line>` apiece. That is a
 * click-triggered amplification vector, not merely a slow page, because the
 * requests carry the viewer's own session.
 *
 * **{@link MAX_OVERLAY_SERIES} is the one authoritative cap.** #993 shipped
 * three independent numbers, and 8 runs x 12 torsions is 96 curves against a
 * 24-curve draw limit — so a link could clear the list caps and still be cut
 * by a second, differently-worded rule. There is now one budget (curves) and
 * everything else is derived from it or bounds a *different* resource and says
 * so:
 *
 * - {@link MAX_OVERLAY_SERIES} — curves drawn. Authoritative. 24 is already
 *   more than anyone reads off one axis.
 * - {@link MAX_OVERLAY_RUNS} — authenticated requests per link. A different
 *   resource (the amplification bound), not a second draw limit; past ~8 runs
 *   the colour ramp repeats and a legend stops being readable anyway.
 * - {@link MAX_OVERLAY_TORSIONS} — DERIVED: the curve budget spent on the
 *   smallest comparison this surface exists for, two runs. The live limit for
 *   a given selection is {@link maxTorsionsFor}, which is what
 *   {@link parseOverlayLink} enforces, so `runs x torsions` can no longer
 *   promise curves the chart will refuse.
 *
 * Truncation is REPORTED, never silent — the whole point of this surface is
 * that it never shows fewer curves than the link named without saying why, and
 * a cap is no exception.
 */
export const MAX_OVERLAY_SERIES = 24;

/** Two runs is the smallest thing worth calling a cross-run comparison. */
const MIN_COMPARISON_RUNS = 2;

export const MAX_OVERLAY_RUNS = 8;
export const MAX_OVERLAY_TORSIONS = MAX_OVERLAY_SERIES / MIN_COMPARISON_RUNS;

/**
 * How many torsions a selection of `runCount` runs may name before it would
 * promise more curves than {@link MAX_OVERLAY_SERIES} draws.
 *
 * Torsions are the axis that gets trimmed, never runs: this is a *cross-run*
 * comparison, so keeping every run the sender named and showing fewer torsions
 * at a time preserves the question being asked (and is where #991 is taking
 * this surface anyway — one torsion at a time, on a slider). The run cap keeps
 * this at 3 or more.
 */
export function maxTorsionsFor(runCount: number): number {
  if (runCount <= 0) return MAX_OVERLAY_TORSIONS;
  return Math.min(MAX_OVERLAY_TORSIONS, Math.floor(MAX_OVERLAY_SERIES / runCount));
}

export interface OverlaySelection {
  /** Ordered, de-duplicated run ids. Order is the legend order. */
  runIds: string[];
  /** Ordered, de-duplicated torsion ids. */
  torsionIds: string[];
  series: OverlaySeriesKind;
}

export const EMPTY_SELECTION: OverlaySelection = {
  runIds: [],
  torsionIds: [],
  series: DEFAULT_SERIES_KIND,
};

function splitList(raw: string | null): string[] {
  if (raw === null) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const value = part.trim();
    // Order-preserving de-duplication: a link is often hand-edited, and the
    // same run twice would otherwise draw the same curve on top of itself and
    // inflate the legend.
    if (value.length === 0 || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function parseSeriesKind(raw: string | null): OverlaySeriesKind {
  return (OVERLAY_SERIES_KINDS as readonly string[]).includes(raw ?? "")
    ? (raw as OverlaySeriesKind)
    : DEFAULT_SERIES_KIND;
}

export interface ParsedOverlayLink {
  selection: OverlaySelection;
  /** Run ids the link named beyond {@link MAX_OVERLAY_RUNS}. Non-empty means
   * the caller MUST tell the user, or the cap becomes the silent drop this
   * surface exists to prevent. */
  droppedRunIds: string[];
  /** Torsion ids beyond {@link torsionLimit}, same contract. */
  droppedTorsionIds: string[];
  /** The torsion limit that actually applied — {@link MAX_OVERLAY_TORSIONS},
   * or less once the retained runs claim more of the curve budget. Exposed so
   * the notice can state the arithmetic instead of naming a constant the
   * user's own selection just narrowed. */
  torsionLimit: number;
}

/**
 * Read a link. Unknown or garbled values degrade to the default rather than
 * erroring — a bad link should still open the surface — and an over-long one
 * is truncated to the caps with the remainder handed back to be reported.
 *
 * Runs are capped first and the torsion limit is then derived from what
 * survived, so what comes out of here is a selection the chart can draw
 * whole: `runIds.length * torsionIds.length <= MAX_OVERLAY_SERIES`. A curve
 * still missing after that is missing for a reason about the science, which
 * is the only kind of gap {@link buildOverlay} should have to report.
 */
export function parseOverlayLink(params: URLSearchParams): ParsedOverlayLink {
  const runIds = splitList(params.get(RUNS_PARAM));
  const torsionIds = splitList(params.get(TORSIONS_PARAM));
  const keptRunIds = runIds.slice(0, MAX_OVERLAY_RUNS);
  const torsionLimit = maxTorsionsFor(keptRunIds.length);
  return {
    selection: {
      runIds: keptRunIds,
      torsionIds: torsionIds.slice(0, torsionLimit),
      series: parseSeriesKind(params.get(SERIES_PARAM)),
    },
    droppedRunIds: runIds.slice(MAX_OVERLAY_RUNS),
    droppedTorsionIds: torsionIds.slice(torsionLimit),
    torsionLimit,
  };
}

/** The selection alone, for callers with nothing to report the truncation to.
 * `parseOverlaySelection(overlaySearchParams(s)) === s` for any selection
 * within the curve budget — which is every selection {@link parseOverlayLink}
 * hands back, so a copied link reopens the same view rather than being
 * narrowed a second time. */
export function parseOverlaySelection(params: URLSearchParams): OverlaySelection {
  return parseOverlayLink(params).selection;
}

/**
 * Write a selection into URL params.
 *
 * `series` is written even when it is the default. The link is a promise
 * about what the recipient will see, and leaving the series implicit would
 * make that promise depend on a default that can change under it; an empty
 * `runs`/`torsions` is instead omitted entirely, because "nothing selected"
 * is the surface's own initial state rather than a claim about content.
 */
export function overlaySearchParams(selection: OverlaySelection): URLSearchParams {
  const params = new URLSearchParams();
  if (selection.runIds.length > 0) params.set(RUNS_PARAM, selection.runIds.join(","));
  if (selection.torsionIds.length > 0) params.set(TORSIONS_PARAM, selection.torsionIds.join(","));
  params.set(SERIES_PARAM, selection.series);
  return params;
}

/**
 * The absolute link for a rendered comparison — what **Copy link** hands over.
 *
 * flowstack#994 item 1: copying `window.location.href` re-propagated whatever
 * arrived, so a truncated link kept naming runs the recipient would not see
 * either, and the sender shared something different from their own screen. The
 * decision, stated here and in the UI: **the link describes the rendered
 * view.** The address bar is deliberately left as it arrived — it is the
 * evidence the truncation notice is talking about — and only the copied link
 * is narrowed.
 *
 * Built from `selection` through {@link overlaySearchParams}, so this is the
 * same encoder the entry links use rather than a second one; anything else in
 * the query string is dropped, because this route's selection IS its query
 * string.
 */
export function overlayLinkFor(selection: OverlaySelection, baseUrl: string): string {
  const url = new URL(baseUrl);
  url.search = overlaySearchParams(selection).toString();
  return url.toString();
}

/** Per-run report state, as the route resolves it from TanStack Query. */
export type OverlayRunState =
  | { kind: "loading" }
  | { kind: "ready"; report: AnalysisReport }
  /** The route returned `available: false` — materialise-on-demand, NOT
   * "this run has no data". The two are different claims and the overlay
   * keeps them apart. */
  | { kind: "not-generated"; reason: string | null }
  | { kind: "unreadable"; errorKind: ApiErrorKind; message: string };

/** One curve: a (run, fragment, torsion) triple read at one series kind. */
export interface OverlaySeries {
  /** recharts `dataKey` into {@link OverlayModel.frame}. Positional, so it is
   * stable for a given selection and safe as a React key. */
  key: string;
  runId: string;
  fragmentId: string;
  torsionId: string;
  /** Legend text — the run (shortened by the caller) and the torsion, plus
   * the fragment when one run matched the torsion in more than one. */
  label: string;
  /** `hsl(var(--chart-series-N))`. */
  color: string;
  /**
   * True when this curve has a hole at an angle it WAS evaluated at, as
   * opposed to simply not covering an angle some other run's grid has. The
   * chart connects across the second and never across the first — see
   * {@link OverlayModel.frame}.
   */
  hasInternalGap: boolean;
  points: { angle: number; value: number }[];
}

/** A (run, torsion) pair the selection asked for that produced no curve. */
export interface OverlayGap {
  runId: string;
  /** `null` when the whole run is unavailable, rather than one torsion. */
  torsionId: string | null;
  /**
   * Set only when the run matched this torsion in more than one fragment and
   * the reason is about ONE of them — the same rule that decides whether a
   * series label names its fragment. `null` otherwise, including for a reason
   * that covers every match (the curve budget) or the whole run.
   *
   * It also makes the identity of a gap line unique: `torsion_id` is unique
   * within a fragment but not across fragments, so without it two fragments
   * failing the same way collide on one key.
   */
  fragmentId: string | null;
  reason: string;
}

export interface OverlayModel {
  /** One row per angle in the UNION of every series' grid, ascending. A
   * series' cell is `null` at an angle it has no sample for. */
  frame: Record<string, number | null>[];
  series: OverlaySeries[];
  gaps: OverlayGap[];
  /** True when the selected runs did not all scan the same angle grid — a
   * real scientific caveat about the comparison, surfaced rather than
   * smoothed over. */
  angleGridsDiffer: boolean;
  /** At least one selected run's report is still in flight. */
  loading: boolean;
}

/**
 * Six categorical series colors (`--chart-series-1..6`, `globals.css`).
 *
 * Cycles past six. A legend that repeats a color is worse than one that does
 * not, but it is far better than an overlay that refuses to draw the seventh
 * curve a link asked for — and the legend, not the color, is what names a
 * series.
 */
export const OVERLAY_SERIES_COLORS = [
  "hsl(var(--chart-series-1))",
  "hsl(var(--chart-series-2))",
  "hsl(var(--chart-series-3))",
  "hsl(var(--chart-series-4))",
  "hsl(var(--chart-series-5))",
  "hsl(var(--chart-series-6))",
] as const;

interface ProfileMatch {
  fragmentId: string;
  profile: DihedralProfile;
}

function matchingProfiles(report: AnalysisReport, torsionId: string): ProfileMatch[] {
  const out: ProfileMatch[] = [];
  for (const fragment of report.fragments) {
    for (const profile of fragment.profiles ?? []) {
      if (profile.torsion_id === torsionId) {
        out.push({ fragmentId: fragment.fragment_id, profile });
      }
    }
  }
  return out;
}

/**
 * Every torsion id a set of ready reports offers, sorted — the torsion
 * picker's options. Union rather than intersection: a torsion only one run
 * fitted is exactly the kind of difference a comparison exists to show, and
 * hiding it would answer the question before the user asked it.
 */
export function availableTorsionIds(states: Iterable<OverlayRunState>): string[] {
  const ids = new Set<string>();
  for (const state of states) {
    if (state.kind !== "ready") continue;
    for (const fragment of state.report.fragments) {
      for (const profile of fragment.profiles ?? []) {
        if (profile.torsion_id) ids.add(profile.torsion_id);
      }
    }
  }
  return [...ids].sort((a, b) => a.localeCompare(b));
}

/** Why a run as a whole contributes nothing, in the run's own terms. */
function describeRunState(state: OverlayRunState): string | null {
  switch (state.kind) {
    case "not-generated":
      return (
        state.reason ??
        "no analysis report has been generated for this run yet — open it and generate one"
      );
    case "unreadable":
      switch (state.errorKind) {
        case "not-found":
          return "this run could not be found — it may have been deleted, or the link may name a run from another tenant";
        case "forbidden":
          return "you do not have permission to view this run";
        case "network":
          return "the server could not be reached for this run";
        case "server":
          return `the server failed to load this run's report (${state.message})`;
        default:
          return `this run's report could not be loaded (${state.message})`;
      }
    default:
      return null;
  }
}

/** The values a series kind reads, or a sentence saying why it has none. */
function readSeries(
  profile: DihedralProfile,
  kind: OverlaySeriesKind
): { values: number[] } | { absent: string } {
  if (kind === "qm") {
    const values = numbers(profile.qm_relative_kcal_per_mol);
    return values.length > 0 ? { values } : { absent: "this profile carries no QM reference" };
  }
  if (kind === "mm") {
    const values = numbers(profile.mm_initial_relative_kcal_per_mol);
    return values.length > 0
      ? { values }
      : { absent: "this profile carries no MM-initial reference" };
  }
  // `fit` is the kind that must never be invented. The absence taxonomy is
  // shared with the per-run panel (`@/lib/dihedral-profile`) precisely so the
  // overlay cannot caption a gap as a scientific decision, or the reverse.
  switch (fitAbsence(profile)) {
    case null:
      return { values: numbers(profile.fitted_relative_kcal_per_mol) };
    case "decided":
      return {
        absent: `standard ${mmSeriesName(profile)} was retained here, so no fitted curve exists`,
      };
    case "misaligned":
      return {
        absent: `the fitted profile arrived on a different angle grid (${numbers(profile.fitted_relative_kcal_per_mol).length} vs ${numbers(profile.angles_deg).length} points)`,
      };
    default:
      return { absent: "no fitted profile was published for this torsion in this run" };
  }
}

export interface BuildOverlayOptions {
  selection: OverlaySelection;
  /** Report state per run id. A run absent from the map counts as loading. */
  runs: ReadonlyMap<string, OverlayRunState>;
  /** Legend text for a run id — the route passes a shortened id. */
  runLabel?: (runId: string) => string;
}

export function buildOverlay({
  selection,
  runs,
  runLabel = (runId) => runId,
}: BuildOverlayOptions): OverlayModel {
  const series: OverlaySeries[] = [];
  const gaps: OverlayGap[] = [];
  let loading = false;

  for (const runId of selection.runIds) {
    const state = runs.get(runId) ?? { kind: "loading" as const };
    if (state.kind === "loading") {
      loading = true;
      continue;
    }
    if (state.kind !== "ready") {
      // One line for the whole run, not one per selected torsion: the reason
      // is about the run, and repeating it per torsion would bury it.
      gaps.push({
        runId,
        torsionId: null,
        fragmentId: null,
        reason: describeRunState(state) ?? "unavailable",
      });
      continue;
    }

    for (const torsionId of selection.torsionIds) {
      const matches = matchingProfiles(state.report, torsionId);
      if (matches.length === 0) {
        gaps.push({
          runId,
          torsionId,
          fragmentId: null,
          reason: "this run's report has no profile for this torsion",
        });
        continue;
      }
      for (const { fragmentId, profile } of matches) {
        if (series.length >= MAX_OVERLAY_SERIES) {
          // The cap is reported through the SAME mechanism as every other
          // reason a curve is absent, so it cannot read as a rendering bug —
          // but ONCE for this pair, not once per fragment it matched
          // (flowstack#994 item 5). The budget is exhausted for every
          // remaining match and the sentence would be identical, so `break`:
          // repeating it says nothing new and buries the reasons that do.
          gaps.push({
            runId,
            torsionId,
            fragmentId: null,
            reason: `not drawn — this comparison draws at most ${MAX_OVERLAY_SERIES} curves`,
          });
          break;
        }
        // `torsion_id` is unique within a fragment's result but not across
        // fragments, so a run CAN legitimately match twice. Both are drawn and
        // both are labelled with their fragment — picking one silently would
        // be the same defect class as dropping a series. A gap about one of
        // those matches names its fragment for the same reason.
        const ambiguous = matches.length > 1;
        const label = ambiguous
          ? `${runLabel(runId)} · ${torsionId} (${fragmentId})`
          : `${runLabel(runId)} · ${torsionId}`;
        const gapFragmentId = ambiguous ? fragmentId : null;
        const read = readSeries(profile, selection.series);
        if ("absent" in read) {
          gaps.push({ runId, torsionId, fragmentId: gapFragmentId, reason: read.absent });
          continue;
        }
        const angles = numbers(profile.angles_deg);
        const n = Math.min(angles.length, read.values.length);
        const points: { angle: number; value: number }[] = [];
        let hasInternalGap = false;
        for (let i = 0; i < n; i += 1) {
          const angle = angles[i];
          const value = read.values[i];
          if (!Number.isFinite(angle)) continue;
          if (!Number.isFinite(value)) {
            // A hole at an angle this curve WAS evaluated at.
            hasInternalGap = true;
            continue;
          }
          points.push({ angle, value });
        }
        if (points.length === 0) {
          gaps.push({
            runId,
            torsionId,
            fragmentId: gapFragmentId,
            reason: `this run's ${SERIES_KIND_LABEL[selection.series]} curve has no plottable points`,
          });
          continue;
        }
        series.push({
          key: `s${series.length}`,
          runId,
          fragmentId,
          torsionId,
          label,
          color: OVERLAY_SERIES_COLORS[series.length % OVERLAY_SERIES_COLORS.length],
          hasInternalGap,
          points,
        });
      }
    }
  }

  const angleUnion = new Set<number>();
  for (const s of series) for (const p of s.points) angleUnion.add(p.angle);
  const angles = [...angleUnion].sort((a, b) => a - b);

  const frame = angles.map((angle) => {
    const row: Record<string, number | null> = { angle };
    for (const s of series) row[s.key] = null;
    return row;
  });
  const rowByAngle = new Map(angles.map((angle, index) => [angle, frame[index]]));
  for (const s of series) {
    for (const p of s.points) {
      const row = rowByAngle.get(p.angle);
      if (row) row[s.key] = p.value;
    }
  }

  const gridSignatures = new Set(series.map((s) => s.points.map((p) => p.angle).join(",")));

  return { frame, series, gaps, angleGridsDiffer: gridSignatures.size > 1, loading };
}
